import React, { useState } from 'react';
import { Mail, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const EmailTest = () => {
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState('Test email from Legal Document Specialist website');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error('Backend URL not configured');
      }
      
      const response = await fetch(`${backendUrl}/api/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test_email: testEmail,
          message: message
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data);
      } else {
        setError(data.detail || data.message || 'Failed to send test email');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: 'var(--bg-card)',
      borderRadius: '12px',
      border: '1px solid var(--border-medium)'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <Mail size={48} color="var(--brand-primary)" style={{ marginBottom: '1rem' }} />
        <h2 style={{ color: 'var(--brand-primary)', marginBottom: '0.5rem' }}>
          Email System Test
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Test the email delivery system for Legal Document Specialist
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '600',
            color: 'var(--text-primary)'
          }}>
            Test Email Address
          </label>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email to receive test message"
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--border-medium)',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '600',
            color: 'var(--text-primary)'
          }}>
            Test Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Custom test message (optional)"
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--border-medium)',
              borderRadius: '6px',
              fontSize: '1rem',
              resize: 'vertical'
            }}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !testEmail}
          style={{
            width: '100%',
            padding: '0.75rem 1.5rem',
            backgroundColor: isLoading || !testEmail ? '#ccc' : 'var(--brand-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: isLoading || !testEmail ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {isLoading ? (
            <>
              <Loader size={18} className="animate-spin" />
              Sending Test Email...
            </>
          ) : (
            <>
              <Send size={18} />
              Send Test Email
            </>
          )}
        </button>
      </form>

      {/* Success Result */}
      {result && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '6px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <CheckCircle size={20} color="#28a745" />
            <strong style={{ color: '#155724' }}>Email Test Successful!</strong>
          </div>
          <p style={{ color: '#155724', margin: '0.5rem 0', fontSize: '0.9rem' }}>
            {result.message}
          </p>
          <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
            <p>✅ Test email sent to: <strong>{result.details?.test_email_sent_to}</strong></p>
            <p>✅ Admin notification sent to: <strong>{result.details?.admin_notification_sent_to}</strong></p>
            <p>✅ From address: <strong>{result.details?.from_address}</strong></p>
            <p>✅ Timestamp: {result.details?.timestamp}</p>
          </div>
        </div>
      )}

      {/* Error Result */}
      {error && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '6px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <AlertCircle size={20} color="#dc3545" />
            <strong style={{ color: '#721c24' }}>Email Test Failed</strong>
          </div>
          <p style={{ color: '#721c24', margin: 0, fontSize: '0.9rem' }}>
            {error}
          </p>
        </div>
      )}

      {/* Current Configuration */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: 'var(--bg-page)',
        border: '1px solid var(--border-light)',
        borderRadius: '6px'
      }}>
        <h4 style={{ color: 'var(--brand-primary)', marginBottom: '0.75rem', fontSize: '1rem' }}>
          Current Email Configuration
        </h4>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <p style={{ margin: '0.25rem 0' }}>
            📧 <strong>Admin Email:</strong> ldsprocess@gmail.com
          </p>
          <p style={{ margin: '0.25rem 0' }}>
            📤 <strong>From Email:</strong> support@legaldocspecialist.com
          </p>
          <p style={{ margin: '0.25rem 0' }}>
            🔧 <strong>Service:</strong> SendGrid Email API
          </p>
          <p style={{ margin: '0.25rem 0' }}>
            📋 <strong>Purpose:</strong> Customer orders, quotes, and notifications
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default EmailTest;